import { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
} from 'firebase/firestore';
import './PlaceLibrary.css';

function Prospects({ user }) {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filteredProspects, setFilteredProspects] = useState([]);
  
  const [editingComment, setEditingComment] = useState(null);
  const [commentText, setCommentText] = useState('');

  useEffect(() => {
    if (user) {
      loadProspects();
    }
  }, [user]);

  useEffect(() => {
    applyFiltersAndSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospects, searchTerm, sortField, sortDirection]);

  const loadProspects = async () => {
    setLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, 'place_details'),
        where('isFavorite', '==', true)
      );
      const querySnapshot = await getDocs(q);
      const prospectsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProspects(prospectsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let result = [...prospects];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (place) =>
          place.name?.toLowerCase().includes(term) ||
          place.formatted_address?.toLowerCase().includes(term) ||
          place.types?.toLowerCase().includes(term)
      );
    }

    result.sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';

      if (sortField === 'rating' || sortField === 'user_ratings_total') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredProspects(result);
  };

  const removeFavorite = async (placeId) => {
    try {
      const placeRef = doc(db, 'place_details', placeId);
      await updateDoc(placeRef, {
        isFavorite: false,
      });
      setProspects((prev) => prev.filter((p) => p.id !== placeId));
    } catch (err) {
      setError(err.message);
    }
  };

  const saveComment = async (placeId) => {
    try {
      const placeRef = doc(db, 'place_details', placeId);
      await updateDoc(placeRef, {
        comment: commentText,
      });
      setProspects((prev) =>
        prev.map((p) =>
          p.id === placeId ? { ...p, comment: commentText } : p
        )
      );
      setEditingComment(null);
      setCommentText('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (!user) {
    return (
      <div className="library-container">
        <p>Please sign in to view prospects.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="library-container">
        <p>Loading prospects...</p>
      </div>
    );
  }

  return (
    <div className="library-container">
      <div className="header-row">
        <h2>Targets</h2>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input-compact"
        />
      </div>

      {error && (
        <div className="error" role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {filteredProspects.length === 0 ? (
        <div className="empty-state">
          <p>No targets yet. Star places in the Leads Board to mark them as recruitment targets.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="places-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('formatted_address')} className="sortable">
                    Address {sortField === 'formatted_address' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('formatted_phone_number')} className="sortable">
                    Phone {sortField === 'formatted_phone_number' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('rating')} className="sortable">
                    Rating {sortField === 'rating' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Website</th>
                  <th>Comment</th>
                </tr>
              </thead>
              <tbody>
                {filteredProspects.map((place) => (
                  <tr key={place.id}>
                    <td>
                      <button
                        className="remove-btn"
                        onClick={() => removeFavorite(place.id)}
                        title="Remove from prospects"
                      >
                        ✕
                      </button>
                    </td>
                    <td>{place.name || '—'}</td>
                    <td>{place.formatted_address || '—'}</td>
                    <td>{place.formatted_phone_number || '—'}</td>
                    <td>{place.rating || '—'}</td>
                    <td>
                      {place.website ? (
                        <a href={place.website} target="_blank" rel="noopener noreferrer">
                          Visit
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="comment-cell">
                      {editingComment === place.id ? (
                        <div className="comment-edit">
                          <textarea
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            placeholder="Add a comment..."
                            rows={2}
                          />
                          <div className="comment-actions">
                            <button onClick={() => saveComment(place.id)}>Save</button>
                            <button onClick={() => setEditingComment(null)}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="comment-display"
                          onClick={() => {
                            setEditingComment(place.id);
                            setCommentText(place.comment || '');
                          }}
                        >
                          {place.comment || 'Click to add comment'}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="results-count">
            Showing {filteredProspects.length} of {prospects.length} prospects
          </div>
        </>
      )}
    </div>
  );
}

export default Prospects;
